import * as React from "react";
import { formatMessage } from "lib/globalize-util";
import { merge, $, media } from "glamor";
import { theme } from "styles/theme";
import { ColorSwatchCircles } from "components/product-collection/product-collection-views/product-collection-tile-view";
import _ from "lodash";
import IconButton from "material-ui/IconButton";
import CartIcon from "components/icons/assortments/cart";
import classNames from "classnames";
import { ICollectionSwatch } from "constants/color-swatches";
import { IconAlertCircle } from "components/icons/assortments/icon-alert-circle";
import Tooltip from "components/tooltip";

const ITEM_HEIGHT = 363;
const ITEM_WIDTH = 134;
const MARGIN = 10;
const IMAGE_HEIGHT = 210;
const IMAGE_WIDTH = ITEM_WIDTH;

interface IProps {
    imageUrl: string;
    onSelectItem: (e: React.MouseEvent) => void;
    primaryAttribute: string;
    secondaryAttribute: string;
    tertiaryAttributes?: string[];
    selected: boolean;
    onItemClick: () => void;
    secondaryImageUrl: string;
    brandName: string;
    additionalColors: ICollectionSwatch[];
    hasAdditionalVariants: boolean;
    allowDuplicateProduct: boolean;
    costPrice?: string;
    unsynced?: boolean;
}

export const SmallTile: React.FunctionComponent<IProps> = ({
    imageUrl,
    onSelectItem,
    primaryAttribute,
    secondaryAttribute,
    tertiaryAttributes = [],
    selected,
    onItemClick,
    secondaryImageUrl,
    brandName,
    additionalColors = [],
    hasAdditionalVariants,
    allowDuplicateProduct,
    costPrice,
    unsynced,
}) => {
    const styles = React.useMemo(() => {
        return merge(
            $(" .node", {
                margin: `${MARGIN}px auto 0`,
                background: theme.palette.canvasColor,
                width: `${ITEM_WIDTH}px`,
                height: `${ITEM_HEIGHT}px`,
                cursor: "pointer",
                transition: "all 0.2s ease-out",
                borderRadius: "5px 5px 0 0",
                position: "relative",
            }),
            $(" .node:hover", {
                borderRadius: "5px",
                boxShadow: "0 4px 16px 0 rgba(45, 45, 45, 0.16)",
            }),
            $(" .images", {
                margin: "0",
                height: `${IMAGE_HEIGHT}px`,
                position: "relative",
                width: "100%",
                overflow: "hidden",
            }),
            $(" .image", {
                height: `${IMAGE_HEIGHT}px`,
            }),
            $(" .primaryImage", {
                width: `${IMAGE_WIDTH}px`,
                height: `${IMAGE_HEIGHT}px`,
                backgroundSize: "contain",
                backgroundRepeat: "no-repeat",
                backgroundPosition: "center center",
                transition: "all 0.2s ease-out",
            }),
            $(" .secondaryImageUrl", {
                position: "absolute",
                top: 0,
                left: 0,
                opacity: 0,
                width: `${IMAGE_WIDTH}px`,
                height: `${IMAGE_HEIGHT}px`,
                backgroundSize: "contain",
                backgroundRepeat: "no-repeat",
                backgroundPosition: "center center",
                transition: "all 0.2s ease-out",
            }),
            $(" .images:hover .secondaryImageUrl", {
                opacity: 1,
            }),
            $(" .brandAttribute", {
                width: `${IMAGE_WIDTH}px`,
                fontFamily: "Lato",
                fontSize: "10px",
                color: theme.palette.darkGrey,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                lineHeight: "1.6",
                textAlign: "center",
                paddingTop: "12px",
            }),
            $(" .primaryAttribute", {
                fontSize: "12px",
                fontWeight: "bold",
                color: theme.palette.textColor,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                textAlign: "center",
            }),
            $(" .additionalInfoFields", {
                fontSize: "11px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                textAlign: "center",
            }),
            $(" .colorSwatchContainer", {
                width: "18px",
                height: "18px",
                borderRadius: "50%",
                position: "relative",
                border: "2px solid white",
            }),
            $(" .colorSwatchContainer.selected", {
                border: `2px solid ${theme.palette.lightGrey}`,
            }),
            $(" .colorSwatch", {
                width: "14px",
                height: "14px",
                borderRadius: "50%",
                position: "absolute",
                border: `1px solid ${theme.palette.lightGrey}`,
                backgroundSize: "18px !important",
            }),
            $(" .cardFooter", {
                position: "absolute",
                bottom: 0,
                left: "8px",
                right: "8px",
                padding: costPrice ? "0 0 4px" : "0 0 10px",
                textAlign: "center",
                borderBottom: `1px solid ${theme.palette.lightGrey}`,
                transition: "all 0.2s ease-out",
            }),
            $(" .node:hover .cardFooter", {
                borderBottom: "1px solid transparent",
            }),
            $(" .priceLabel", {
                fontSize: "11px !important",
            }),
            $(" .priceAttribute", {
                fontSize: "12px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                fontWeight: "bold",
                textAlign: "center",
            }),
            $(" .colorCarousel", {
                height: "18px",
                display: "flex",
                justifyContent: "center",
                paddingTop: "4px",
            }),
            $(" .favorite", {
                position: "absolute !important",
                bottom: "0px",
                right: "0px",
                zIndex: 10,
                opacity: 0,
            }),
            media(
                "(hover: none)",
                $(" .favorite", {
                    opacity: 1,
                })
            ),
            $(".selected .favorite", {
                opacity: 1,
            }),
            $(".selected .favorite:hover svg", {
                fill: `${theme.palette.accent1Color} !important`,
            }),
            $(".selected .favorite:disabled svg", {
                opacity: 0.5,
                fill: `${theme.palette.accent1Color} !important`,
            }),
            $(":hover .favorite", {
                opacity: 1,
            }),
            $(".favorite:hover svg", {
                fill: `${theme.palette.darkestGrey} !important`,
            }),
            $(" .favorite-left", {
                position: "absolute !important",
                bottom: "0px",
                left: "0px",
                zIndex: 10,
            }),
            $(" .hasAdditionalVariants", {
                height: "15px",
                lineHeight: "15px",
                borderRadius: "4px",
                backgroundColor: theme.palette.lightestGrey,
                color: theme.palette.grey,
                textAlign: "center",
                fontSize: "10px",
                fontStyle: "italic",
                marginTop: "5px",
            })
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const swatches = React.useMemo(() => {
        const sortedColors = _.sortBy(additionalColors, "selected").reverse();
        const _swatches = sortedColors.map((swatchProps, i) => {
            if (i < 5) {
                return <ColorSwatchCircles key={i} {...swatchProps} />;
            } else {
                return null;
            }
        });
        return _swatches;
    }, [additionalColors]);

    return (
        <div {...styles} className={classNames({ selected })} onClick={onItemClick}>
            <div className="node">
                {imageUrl && (
                    <div className="images">
                        <div
                            className="primaryImage"
                            style={{
                                width: `${IMAGE_WIDTH}px`,
                                height: `${IMAGE_HEIGHT}px`,
                                backgroundImage: `url(${imageUrl})`,
                                backgroundSize: "contain",
                                backgroundRepeat: "no-repeat",
                                backgroundPosition: "center center",
                            }}
                        />
                        {secondaryImageUrl && (
                            <div
                                className="secondaryImageUrl"
                                style={{
                                    width: `${IMAGE_WIDTH}px`,
                                    height: `${IMAGE_HEIGHT}px`,
                                    backgroundImage: `url(${secondaryImageUrl})`,
                                    backgroundSize: "contain",
                                    backgroundRepeat: "no-repeat",
                                    backgroundPosition: "center center",
                                    backgroundColor: "white",
                                }}
                            />
                        )}
                    </div>
                )}
                {!imageUrl && <div className="image" />}
                <div className="brandAttribute">{brandName}</div>
                <div className="primaryAttribute">{primaryAttribute}</div>
                {tertiaryAttributes.map((val, i) => (
                    <div key={i} className="additionalInfoFields">
                        {val}
                    </div>
                ))}
                {swatches.length > 1 && <div className="colorCarousel">{swatches}</div>}
                {hasAdditionalVariants && (
                    <div className="hasAdditionalVariants">{formatMessage("More colors available")}</div>
                )}
                <div className="cardFooter">
                    {costPrice && (
                        <>
                            <div className="priceLabel">{formatMessage("Cost")}</div>
                            <div className="priceAttribute">{costPrice}</div>
                        </>
                    )}
                    <div className="priceLabel">{formatMessage("Retail")}</div>
                    <div className="priceAttribute">{secondaryAttribute}</div>
                </div>
                {unsynced && (
                    <Tooltip
                        title={formatMessage("Not yet synced. Additional data and imagery is not yet available.")}
                        placement="top"
                    >
                        <IconButton className="favorite-left">
                            <IconAlertCircle color={theme.palette.darkGrey} />
                        </IconButton>
                    </Tooltip>
                )}
                {onSelectItem && (
                    <IconButton
                        className="favorite"
                        onClick={onSelectItem}
                        disabled={selected && !allowDuplicateProduct}
                    >
                        <CartIcon color={selected ? theme.palette.accent1Color : theme.palette.darkGrey} />
                    </IconButton>
                )}
            </div>
        </div>
    );
};

(SmallTile as any).height = ITEM_HEIGHT + MARGIN * 2;
(SmallTile as any).width = ITEM_WIDTH + MARGIN * 2;
(SmallTile as any).horizontalOffset = 0;
(SmallTile as any).verticalOffset = 0;
